/**
 * Screen 5 — programme details.
 *
 * Every price tier is shown before the member commits, so nothing new can
 * appear at checkout. When a non-member price exists, both are shown: a
 * member seeing only the higher one has no idea what joining is worth, and a
 * guest meeting the higher one at checkout feels tricked.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { LocalisedField } from "../components/Localised";
import { ProgramArt } from "../components/ProgramArt";
import { ErrorNote, Note, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import { api, OfflineError, type ProgramDetail as Detail } from "../lib/api";
import { formatDate, formatIdr, formatTime } from "../lib/format";

export default function ProgramDetail({ locale }: { locale: Locale }) {
  const { slug = "" } = useParams();
  const { t } = useTranslation();
  const [program, setProgram] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .program(slug, locale)
      .then(setProgram)
      .catch((err) =>
        setError(err instanceof OfflineError ? t("error.network") : t("error.generic")),
      );
  }, [slug, locale, t]);

  if (!program) {
    return (
      <Shell area="programs" title={t("programs.title")}>
        {error ? <ErrorNote>{error}</ErrorNote> : <Note>{t("common.loading")}</Note>}
      </Shell>
    );
  }

  return (
    <Shell area="programs" title={t("programs.title")}>
      <ProgramArt
        poster={program.poster_url}
        category={program.category}
        labelKey={program.category_label_key}
        tall
      />

      <LocalisedField
        content={program.text}
        field="name"
        as="h2"
        className="screen__subtitle"
        note
      />

      {/* Date, time and hall are one fact, not three loose lines. Grouped and
          weighted the way a programme prints them: when, then where. */}
      <p className="when">
        <span className="when__date">{formatDate(program.date_time, locale)}</span>
        <span className="when__time">{formatTime(program.date_time)}</span>
        <span className="when__venue">{program.venue}</span>
      </p>

      <h3 className="section">{t("program.about")}</h3>
      <LocalisedField content={program.text} field="description" />

      <h3 className="section">{t("program.performers")}</h3>
      <LocalisedField content={program.text} field="performers" />

      <ul className="tiers">
        {program.tiers.map((tier) => (
          <li key={tier.id}>
            <span>{t(tier.label_key)}</span>
            <span className="money">{formatIdr(tier.price_idr)}</span>
            {tier.non_member_price_idr !== null &&
              tier.non_member_price_idr !== tier.member_price_idr && (
                <span className="fallback-note">
                  {t("programs.members_only")}: {formatIdr(tier.member_price_idr)}
                </span>
              )}
          </li>
        ))}
      </ul>

      {program.sold_out ? (
        <span className="badge badge--muted">{t("programs.sold_out")}</span>
      ) : (
        <Link className="btn btn--primary" to={`/programs/${program.slug}/seats`}>
          {t("programs.book_now")}
        </Link>
      )}
    </Shell>
  );
}
